import React from 'react';
import { cn } from '@/lib/utils';

/**
 * Wraps a widget component for short slider display
 * Applies beautiful, compact styling for the short (top) slider
 * with celestial aesthetic that matches the Dreamspell skin
 */
export function ShortWidgetWrapper({ 
  children, 
  title,
  icon,
  accent = 'amber'
}: { 
  children: React.ReactNode;
  title?: string;
  icon?: React.ReactNode;
  accent?: 'amber' | 'teal' | 'rose' | 'emerald' | 'purple';
}) {
  return (
    <div className="w-full h-full min-h-0 flex flex-col items-center justify-center overflow-hidden relative">
      {/* Subtle glow effect */}
      <div 
        className="absolute inset-0 pointer-events-none"
        style={{
          background: 'radial-gradient(ellipse 80% 60% at 50% 30%, rgba(244,208,63,0.04) 0%, transparent 70%)',
        }}
      />
      
      {/* Content container */}
      <div className={cn(
        "relative w-full h-full min-h-0",
        "rounded-xl",
      )}>
        {children}
      </div>
    </div>
  );
}

/**
 * Beautiful compact widget card for short slider
 * Self-contained with header, icon, and content
 * NO progress bars - uses semantic details instead
 */
export function ShortWidgetCard({
  title,
  icon,
  children,
  accent = 'amber',
  details,
}: {
  title: string;
  icon?: React.ReactNode;
  children: React.ReactNode;
  accent?: 'amber' | 'teal' | 'rose' | 'emerald' | 'purple';
  details?: Array<{ label: string; value: string | number; color?: string }>;
}) {
  const accentColors = {
    amber: { border: 'border-l-amber-400', text: 'text-amber-200', glow: 'rgba(244,208,63,0.15)' },
    teal: { border: 'border-l-teal-400', text: 'text-teal-200', glow: 'rgba(126,184,181,0.15)' },
    rose: { border: 'border-l-rose-400', text: 'text-rose-200', glow: 'rgba(244,63,94,0.15)' },
    emerald: { border: 'border-l-emerald-400', text: 'text-emerald-200', glow: 'rgba(52,211,153,0.15)' },
    purple: { border: 'border-l-purple-400', text: 'text-purple-200', glow: 'rgba(168,85,247,0.15)' },
  };

  const colors = accentColors[accent];

  return (
    <div 
      className={cn(
        "w-full h-full flex flex-col gap-2 px-4 py-3 relative overflow-hidden",
        "border-l-2",
        colors.border
      )}
    >
      {/* Header with icon and title */}
      <div className="flex items-center justify-between shrink-0">
        <div className="flex items-center gap-2">
          {icon && (
            <div 
              className={cn("shrink-0", colors.text)}
              style={{ filter: `drop-shadow(0 0 6px ${colors.glow})` }}
            >
              {icon}
            </div>
          )}
          <span 
            className={cn("text-xs font-semibold tracking-wide", colors.text)}
          >
            {title}
          </span>
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 w-full min-h-0 overflow-auto">
        {children}
      </div>

      {/* Optional detail chips - replaces progress bars */}
      {details && details.length > 0 && (
        <div className="flex items-center gap-2 flex-wrap shrink-0 pt-1 border-t border-white/5">
          {details.map((detail, i) => (
            <div 
              key={i}
              className="flex items-center gap-1.5 text-[9px]"
            >
              <span className="text-white/40">{detail.label}:</span>
              <span 
                className="font-semibold tabular-nums"
                style={{ color: detail.color || colors.glow.replace('0.15', '1') }}
              >
                {detail.value}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Creates a compact version of a widget for the short slider
 * Returns a simplified, text-based representation when full component is too large
 */
export function createShortWidgetVersion(
  widgetId: string,
  fullComponent: React.ReactNode,
  fallbackContent: React.ReactNode
): React.ReactNode {
  return fullComponent;
}
